import { useDragula } from '@beesoft/react-dragula';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import clsx from 'clsx';
import { cloneDeep, debounce, isEqual } from 'lodash';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { getAttributeValueRecursive, hasClassNameRecursive } from '../common-functions';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import type { JsonData } from '../generic-types';
import { Api } from './api/api';
import type { DefaultDataGridMenuItems } from './data-grid-cog-menu.component';
import DataGridCogMenu from './data-grid-cog-menu.component';
import type { RowTemplateType } from './data-grid-data-row.component';
import DataGridData from './data-grid-data.component';
import { calculateRowWidth, setColumnDefaultValues } from './data-grid-functions';
import DataGridHeader from './data-grid-header.component';
import type { DataGridSettingsMenuItem } from './data-grid-settings-menu.component';
import type { DataGridColumnSettings, DataGridRowIndicator, DataGridSettings } from './data-grid-settings.interface';
import useTestTag from '../../common/hooks/use-test-tag';
import type { OnResizeFunctionParameters } from '../../common/hooks/use-resize-observer';
import useResizeObserver from '../../common/hooks/use-resize-observer';
import { useEvent } from '../../common/hooks/use-event';
import { changeSignalColumnSize } from './functions/data-grid-signal-functions';
import useOdinMemo from '../../common/hooks/use-odin-memo';
import { combineRefs } from '../../common/functions/combine-refs';
import type { OdinKey } from '../../common/types/odin-key.type';

export interface DataGridProps {
  gridSettings: DataGridSettings;
  data: JsonData | OdinDataRetrieval;
  customData?: JsonData;
  rowIndicator?: DataGridRowIndicator;
  rowTemplate?: RowTemplateType;
  showSettings?: boolean;
  /**
   * Event handler that fires when a row is selected or unselected.
   * @param gridId {string} - The unique Id for this grid component; used when multiple grid are on a single page.
   * @param selectedRows {JsonData} - The data representing the selected rows.
   */
  onRowSelectionChange?: (gridId: string, selectedRows: JsonData) => void;
  onCogMenuItemClicked?: (itemName: string) => void;
  onViewChange?: (hasChanged: boolean) => void;
  customSettingsMenuItems?: Array<DataGridSettingsMenuItem>;
  visibleDefaultSettings?: Array<DefaultDataGridMenuItems>;
  showGridColumnsOnSettingsMenu?: boolean;
  showGridMenu?: boolean;
  isPaginated?: boolean;
}

export interface DataGridRef {
  api: Api;
}

function DataGrid(props: DataGridProps, ref: Ref<DataGridRef>) {
  const {
    gridSettings,
    data,
    customData,
    rowIndicator,
    rowTemplate,
    showSettings = true,
    onRowSelectionChange,
    onCogMenuItemClicked,
    onViewChange,
    customSettingsMenuItems,
    visibleDefaultSettings,
    showGridColumnsOnSettingsMenu = true,
    showGridMenu,
    isPaginated = true,
  } = props;

  const [columnsChanged, setColumnsChanged] = useState(false);

  const gridId = useRef(v4());
  const forceResetColumnMap = useRef<Map<OdinKey, boolean>>(new Map());
  const columnWidthAdjustedRef = useRef(false);

  const initialFieldFilters = useOdinMemo(
    () => [...(gridSettings.initialFieldFilters ?? [])],
    [gridSettings.initialFieldFilters]
  );

  // these are replacing the same items in the reducer data
  const originalColumns = useRef<Array<DataGridColumnSettings>>([]);

  const gridScroller = useSignal<HTMLElement | null>(null);
  const filterSignal = useSignal<Array<ContinuousScrollerFilter>>(initialFieldFilters);
  const sortSignal = useSignal<Array<ContinuousScrollerSort>>(gridSettings.initialSortedFields || []);

  const tagCreator = useTestTag('data-grid');

  const handleWindowResize = (width?: number) => {
    if (gridSettings.autoSizeColumns && width) {
      changeSignalColumnSize(columns, rowWidth, indicatorRowWidth, availableWidth, gridSettings.columns, width);
    } else if (availableWidth.value === 0 && width) {
      availableWidth.value = width - (indicatorRowWidth.value ?? 0);
    }
  };

  const debounceHandleWindowResize = debounce((width: number) => {
    handleWindowResize(width);
  }, 100);

  const handleResize = useEvent(({ width }: OnResizeFunctionParameters) => {
    if (width) {
      debounceHandleWindowResize(width);
    }
  });

  const columns = useSignal(setColumnDefaultValues(gridSettings.columns, gridSettings.autoSizeColumns));
  const rowWidth = useSignal(calculateRowWidth(gridSettings.columns));
  const indicatorRowWidth = useSignal(0);
  const availableWidth = useSignal(0);
  const staticData = useSignal<JsonData>([]);
  const selectedRows = useSignal<JsonData>([]);
  const highlightedRows = useSignal<JsonData>([]);
  const pageSize = useSignal(gridSettings.initialPageSize || 50);
  const [optionsContainerRef] = useResizeObserver(handleResize);

  // this is defined here, so it can be used in the useDragula hook below. If not it will have an error about a function
  // not defined before its used.
  const movesListener = useCallback((_element?: Element, _container?: Element, handle?: Element) => {
    return (
      handle !== undefined &&
      hasClassNameRecursive(handle, 'column-main') &&
      getAttributeValueRecursive(handle, 'data-draggable') === 'true'
    );
  }, []);

  useDragula([], {
    moves: movesListener,
  });

  const resetView = useEvent(() => {
    columnWidthAdjustedRef.current = false;

    forceResetColumnMap.current.clear();

    gridSettings.columns.forEach((column) => {
      forceResetColumnMap.current.set(column.id, true);
    });

    originalColumns.current = cloneDeep(gridSettings.columns);
    columns.value = originalColumns.current;
    sortSignal.value = gridSettings.initialSortedFields || [];
    filterSignal.value = initialFieldFilters || [];
  });

  const apiRef = useRef<Api>(
    new Api(resetView, filterSignal, sortSignal, columns, staticData, selectedRows, highlightedRows)
  );

  useSignalEffect(() => {
    setColumnsChanged(
      gridSettings.columns.some(
        (column, index) => column.id !== columns.value[index]?.id || column.visible !== columns.value[index]?.visible
      )
    );
  });

  useEffect(() => {
    if (!isEqual(gridSettings.columns, originalColumns.current)) {
      originalColumns.current = cloneDeep(gridSettings.columns);
      columns.value = originalColumns.current;
      onViewChange?.(false);
      filterSignal.value = initialFieldFilters || [];
      sortSignal.value = gridSettings.initialSortedFields || [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger on gridSettings.columns change, other deps are stable signals or props that shouldn't trigger re-run
  }, [gridSettings.columns]);

  useSignalEffect(() => {
    const sortChanged = !isEqual(sortSignal.value, gridSettings.initialSortedFields);
    const filtersChanged = !isEqual(filterSignal.value, initialFieldFilters);

    onViewChange?.(columnsChanged || sortChanged || filtersChanged);
  });

  useSignalEffect(() => {
    onRowSelectionChange?.(gridId.current, selectedRows.value);
  });

  useEffect(() => {
    gridScroller.value?.scrollTo(0, 0);

    if (Array.isArray(data)) {
      staticData.value = data;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridScroller and staticData are signals, only re-run when data prop changes
  }, [data]);

  useEffect(() => {
    if (gridSettings.autoSizeColumns) {
      handleWindowResize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridSettings.autoSizeColumns is stable, handleWindowResize is a local function that shouldn't trigger re-runs
  }, [availableWidth.value, columnsChanged]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce wraps the callback, dependencies tracked internally; gridScroller is a signal
  const onGridElementCreated = useCallback(
    debounce((element: HTMLElement) => {
      if (gridScroller.value === null || !gridScroller.value.isSameNode(element)) {
        gridScroller.value = element;
      }
    }, 75),
    []
  );

  useImperativeHandle(ref, () => ({
    api: apiRef.current,
  }));

  const dynamicProperties = useOdinMemo(() => {
    const properties: Record<string, unknown> = {};

    if (!Array.isArray(data)) {
      properties['search'] = data;
    }

    return properties;
  }, [data]);

  const wrapperClasses = clsx('odin-w-full odin-text-lg odin-overflow-hidden odin-relative odin-mt-2', {
    'odin-pr-10': showSettings,
    'odin-h-full odin-max-h-[inherit]': gridSettings.fullHeight === true,
    'odin-h-80': !gridSettings.fullHeight,
  });
  const gridClasses = clsx('odin-w-full odin-h-full odin-max-h-[inherit] odin-overflow-auto custom-scroll');

  const gridElementRef = combineRefs((element) => element && onGridElementCreated(element), tagCreator('wrapper'));

  return (
    <div className={wrapperClasses} ref={tagCreator()}>
      <div ref={gridElementRef} className={gridClasses}>
        <DataGridHeader
          columns={columns}
          rowWidth={rowWidth}
          indicatorRowWidth={indicatorRowWidth}
          availableWidth={availableWidth}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          scrollDetection={gridSettings.scrollDetection}
          useIndicatorColumn={rowIndicator !== undefined}
          headerRowIndicator={gridSettings.headerRowIndicator}
          forceResetColumnMap={forceResetColumnMap.current}
          {...dynamicProperties}
        />
        <DataGridData
          columns={columns}
          selectedRows={selectedRows}
          highlightedRows={highlightedRows}
          staticData={staticData}
          pageSize={pageSize}
          optionsContainerRef={optionsContainerRef}
          components={gridSettings.components}
          rowIndicator={rowIndicator}
          filterLocation={gridSettings.filterLocation}
          data={data}
          customData={customData}
          minimumPageCount={gridSettings.minimumPageCount}
          stopRequestsOnNoData={gridSettings.stopRequestsOnNoData}
          rowIsClickable={gridSettings.rowIsClickable}
          sortFunction={gridSettings.sortFunction}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          api={apiRef.current}
          scrollerElement={gridScroller}
          rowTemplate={rowTemplate}
          allowCellScrollBar={gridSettings.allowCellScrollBar}
          autoSizeColumns={gridSettings.autoSizeColumns}
          columnWidthAdjustedRef={columnWidthAdjustedRef}
          isPaginated={isPaginated}
        />
      </div>

      {showSettings && (
        <DataGridCogMenu
          columns={columns}
          rowWidth={rowWidth}
          indicatorRowWidth={indicatorRowWidth}
          filters={filterSignal}
          resetView={resetView}
          onCogMenuItemClicked={onCogMenuItemClicked}
          customSettingsMenuItems={customSettingsMenuItems}
          visibleDefaultSettings={visibleDefaultSettings}
          showGridColumnsOnSettingsMenu={showGridColumnsOnSettingsMenu}
          scrollDetection={gridSettings.scrollDetection}
          unmountOnExit={true}
          mountOnEnter={true}
          api={apiRef.current}
          autoSizeColumns={gridSettings.autoSizeColumns}
          usesIndicatorColumn={rowIndicator !== undefined}
          showGridMenu={showGridMenu}
        />
      )}
    </div>
  );
}

export default forwardRef(DataGrid);
